import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, PencilSimple, ArrowSquareOut } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/*
  Scrolling marquee banner shown at the top of every authenticated page.
  Single source of truth: GET /api/announcement.

  - Hidden when {enabled: false} or {text: ""}.
  - Animation pauses on hover so users can read / click the link.
  - "X" button dismisses LOCALLY (sessionStorage) — comes back next session
    so important news isn't accidentally muted forever.
  - Admins see a small pencil to open the editor inline.
*/
export default function AnnouncementBanner({ user, onAdminEdit }) {
  const [banner, setBanner] = useState(null);
  const [dismissed, setDismissed] = useState(
    typeof window !== "undefined" && sessionStorage.getItem("announcement_dismissed") === "1"
  );

  useEffect(() => {
    let alive = true;
    const fetchBanner = async () => {
      try {
        const res = await axios.get(`${API}/announcement`);
        if (alive) setBanner(res.data);
      } catch (e) {
        // Silent — banner is non-critical
      }
    };
    fetchBanner();
    // Re-fetch every 60s so admin edits propagate without a hard reload
    const id = setInterval(fetchBanner, 60_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  const visible = banner && banner.enabled && (banner.text || banner.link_text);
  const isAdmin = user?.role === "admin";

  // When the banner is hidden (disabled or empty), regular users see nothing
  // — but admins still need a way to OPEN the editor to create the first
  // banner. So we render a slim "Add announcement" pill-strip for admins.
  if (!visible) {
    if (!isAdmin) return null;
    return (
      <div
        className="w-full bg-zinc-50 border-b border-zinc-200 px-4 py-1.5 flex items-center justify-end"
        data-testid="announcement-admin-empty-strip"
      >
        <button
          onClick={onAdminEdit}
          className="text-[11px] font-semibold text-[#E91E8C] hover:text-[#c91a7b] inline-flex items-center gap-1.5 px-2 py-1 rounded-full hover:bg-[#FDF2F8] transition-all"
          data-testid="announcement-edit-btn"
          title="Create or edit the platform announcement"
        >
          <PencilSimple size={12} weight="bold" />
          Add announcement banner
        </button>
      </div>
    );
  }

  if (dismissed) {
    // Banner is set + enabled but THIS user dismissed it for the session.
    // Still show admins the pencil so they can re-enter the editor without
    // having to hard-reload or clear sessionStorage.
    if (!isAdmin) return null;
    return (
      <div
        className="w-full bg-zinc-50 border-b border-zinc-200 px-4 py-1.5 flex items-center justify-end"
        data-testid="announcement-admin-dismissed-strip"
      >
        <button
          onClick={onAdminEdit}
          className="text-[11px] font-semibold text-[#E91E8C] hover:text-[#c91a7b] inline-flex items-center gap-1.5 px-2 py-1 rounded-full hover:bg-[#FDF2F8] transition-all"
          data-testid="announcement-edit-btn"
          title="Edit the active banner"
        >
          <PencilSimple size={12} weight="bold" />
          Edit announcement
        </button>
      </div>
    );
  }

  const dismiss = () => {
    sessionStorage.setItem("announcement_dismissed", "1");
    setDismissed(true);
  };

  return (
    <div
      className="relative w-full bg-gradient-to-r from-[#E91E8C] via-[#ff3da4] to-[#E91E8C] text-white overflow-hidden"
      data-testid="announcement-banner"
    >
      <div className="flex items-center gap-3 px-4 py-2">
        {/* Marquee */}
        <div className="flex-1 overflow-hidden relative">
          <div
            className="whitespace-nowrap inline-block animate-tbb-marquee"
            style={{ animationDuration: "28s" }}
            data-testid="announcement-marquee"
          >
            <span className="inline-flex items-center gap-3 mr-16">
              <span className="text-sm font-semibold tracking-wide">{banner.text}</span>
              {banner.link_url ? (
                <a
                  href={banner.link_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 bg-white/15 hover:bg-white/30 transition-all rounded-full px-3 py-0.5 text-xs font-bold backdrop-blur-sm"
                  data-testid="announcement-link"
                >
                  {banner.link_text || "Join now"}
                  <ArrowSquareOut size={12} weight="bold" />
                </a>
              ) : null}
            </span>
            {/* Duplicate so the loop seam is invisible */}
            <span className="inline-flex items-center gap-3 mr-16">
              <span className="text-sm font-semibold tracking-wide">{banner.text}</span>
              {banner.link_url ? (
                <span className="inline-flex items-center gap-1 bg-white/15 rounded-full px-3 py-0.5 text-xs font-bold">
                  {banner.link_text || "Join now"}
                  <ArrowSquareOut size={12} weight="bold" />
                </span>
              ) : null}
            </span>
          </div>
        </div>

        {/* Admin pencil + dismiss */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {isAdmin ? (
            <button
              onClick={onAdminEdit}
              className="p-1.5 hover:bg-white/20 rounded-full transition-all"
              title="Edit announcement"
              data-testid="announcement-edit-btn"
              aria-label="Edit announcement"
            >
              <PencilSimple size={14} weight="bold" />
            </button>
          ) : null}
          <button
            onClick={dismiss}
            className="p-1.5 hover:bg-white/20 rounded-full transition-all"
            title="Hide for this session"
            data-testid="announcement-dismiss-btn"
            aria-label="Dismiss announcement"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}

/*
  Admin editor modal. Mounted at the App level so admins can update the
  banner from anywhere (toggle the pencil, edit, save).
*/
export function AnnouncementEditorModal({ open, onClose, onSaved }) {
  const [form, setForm] = useState({
    enabled: false,
    text: "",
    link_text: "",
    link_url: "",
    slug: "",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setLoading(true);
    axios
      .get(`${API}/announcement`)
      .then((res) => setForm({
        enabled: !!res.data.enabled,
        text: res.data.text || "",
        link_text: res.data.link_text || "",
        link_url: res.data.link_url || "",
        slug: res.data.slug || "",
      }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await axios.put(`${API}/announcement`, form);
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  // Surface the user-facing short link so admin can copy it for marketing.
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const shortLink = form.slug ? `${origin}/go/${form.slug}` : "";

  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="announcement-editor"
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-zinc-400 hover:text-zinc-700 w-8 h-8 rounded-full hover:bg-zinc-100 flex items-center justify-center"
          aria-label="Close"
        >
          <X size={16} weight="bold" />
        </button>

        <h3 className="text-lg font-bold text-zinc-900 mb-1">Edit announcement banner</h3>
        <p className="text-xs text-zinc-500 mb-5">
          Shows as a scrolling banner at the top of every page.
        </p>

        {loading ? (
          <p className="text-sm text-zinc-500 py-8 text-center">Loading…</p>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3 bg-zinc-50 rounded-lg px-3 py-2.5">
              <input
                id="ann-enabled"
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                className="w-4 h-4 accent-[#E91E8C]"
                data-testid="announcement-enabled"
              />
              <label htmlFor="ann-enabled" className="text-sm font-medium text-zinc-700 cursor-pointer">
                Show banner to all users
              </label>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5">Banner text</label>
              <input
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                placeholder="Join my weekly Zoom: Your AI Advantage — Tuesdays 7pm ET"
                className="w-full px-3 py-2 border border-zinc-200 rounded-lg text-sm focus:border-[#E91E8C] focus:ring-1 focus:ring-[#E91E8C] outline-none"
                maxLength={240}
                data-testid="announcement-text"
              />
              <p className="text-[11px] text-zinc-400 mt-1">{form.text.length}/240</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5">Button label</label>
                <input
                  value={form.link_text}
                  onChange={(e) => setForm({ ...form, link_text: e.target.value })}
                  placeholder="Register"
                  className="w-full px-3 py-2 border border-zinc-200 rounded-lg text-sm focus:border-[#E91E8C] focus:ring-1 focus:ring-[#E91E8C] outline-none"
                  maxLength={60}
                  data-testid="announcement-link-text"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5">Short-link slug</label>
                <input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                  placeholder="zoom"
                  className="w-full px-3 py-2 border border-zinc-200 rounded-lg text-sm focus:border-[#E91E8C] focus:ring-1 focus:ring-[#E91E8C] outline-none"
                  maxLength={40}
                  data-testid="announcement-slug"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5">Destination URL</label>
              <input
                value={form.link_url}
                onChange={(e) => setForm({ ...form, link_url: e.target.value })}
                placeholder="https://us06web.zoom.us/meeting/register/…"
                className="w-full px-3 py-2 border border-zinc-200 rounded-lg text-sm focus:border-[#E91E8C] focus:ring-1 focus:ring-[#E91E8C] outline-none font-mono"
                maxLength={500}
                data-testid="announcement-link-url"
              />
              {shortLink && form.link_url ? (
                <p className="text-[11px] text-zinc-500 mt-1.5">
                  Public short link → <span className="font-mono text-[#E91E8C]">{shortLink}</span>
                </p>
              ) : null}
            </div>

            {error ? <p className="text-xs text-red-500">{error}</p> : null}

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-zinc-600 hover:text-zinc-900"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="px-5 py-2 bg-[#E91E8C] text-white text-sm font-semibold rounded-lg hover:bg-[#c91a7b] disabled:opacity-60"
                data-testid="announcement-save-btn"
              >
                {saving ? "Saving…" : "Save banner"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
